import React, { useState, useRef, useEffect } from 'react';

const getOptionValue = (opt) => {
    if (opt === null || opt === undefined) return '';
    if (typeof opt === 'object') return (opt._id ?? opt.id ?? opt.value ?? '')?.toString();
    return opt?.toString();
};

const getOptionLabel = (opt) => {
    if (opt === null || opt === undefined) return '';
    if (typeof opt === 'object') return opt.name ?? opt.label ?? opt.title ?? '';
    return opt?.toString();
};

const SearchableSelect = ({
    name,
    value,
    options = [],
    onChange,
    onBlur,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    disabled = false,
    hasError = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Auto-focus search input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const valStr = getOptionValue(value);
    const targetLabel = (typeof value === 'object' ? getOptionLabel(value) : value?.toString() || '')?.trim();

    const selectedOption = (options || []).find((opt) => {
        const optVal = getOptionValue(opt);
        if (optVal && valStr && optVal === valStr) return true;
        if (optVal && value?.toString() && optVal === value.toString()) return true;
        const optLabel = getOptionLabel(opt)?.trim();
        if (optLabel && targetLabel && optLabel.toLowerCase() === targetLabel.toLowerCase()) return true;
        return false;
    });

    const displayLabel = selectedOption
        ? getOptionLabel(selectedOption)
        : (typeof value === 'object' && getOptionLabel(value)
            ? getOptionLabel(value)
            : (targetLabel && (options || []).length === 0 ? targetLabel : placeholder));

    const filteredOptions = (options || [])
        .filter((opt) => {
            const label = getOptionLabel(opt);
            return label.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .sort((a, b) => {
            const labelA = getOptionLabel(a);
            const labelB = getOptionLabel(b);
            return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' });
        });

    const handleSelect = (selectedVal) => {
        if (onChange) {
            onChange({
                target: {
                    name,
                    value: selectedVal,
                },
            });
        }
        setIsOpen(false);
        setSearchTerm('');
        if (onBlur) {
            onBlur({ target: { name } });
        }
    };

    return (
        <div
            ref={containerRef}
            style={{ position: 'relative', width: '100%', marginTop: '4px' }}
            className={className}
        >
            <div
                onClick={() => {
                    if (!disabled) {
                        setIsOpen((prev) => !prev);
                    }
                }}
                style={{
                    width: '100%',
                    height: '40px',
                    padding: '8px 12px',
                    fontSize: '14px',
                    color: selectedOption ? '#1D1F2C' : '#667085',
                    border: isOpen
                        ? '1px solid #512DA8'
                        : hasError
                        ? '1px solid red'
                        : '1px solid #ced4da',
                    borderRadius: '8px',
                    backgroundColor: disabled ? '#F8F9FA' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    userSelect: 'none',
                }}
            >
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        paddingRight: '8px',
                    }}
                >
                    {displayLabel}
                </span>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#667085"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0,
                    }}
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </div>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: '#ffffff',
                        border: '1px solid #ced4da',
                        borderRadius: '8px',
                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                        zIndex: 9999,
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            padding: '8px',
                            borderBottom: '1px solid #E0E2E7',
                            backgroundColor: '#FAFAFA',
                        }}
                    >
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                    setIsOpen(false);
                                }
                            }}
                            style={{
                                width: '100%',
                                padding: '7px 10px',
                                fontSize: '13px',
                                border: '1px solid #ced4da',
                                borderRadius: '6px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                backgroundColor: '#fff',
                            }}
                        />
                    </div>

                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {(!searchTerm || 'select...'.includes(searchTerm.toLowerCase())) && (
                            <div
                                onClick={() => handleSelect('')}
                                style={{
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    color: '#858D9D',
                                    cursor: 'pointer',
                                    backgroundColor: !value ? '#F0F1F3' : 'transparent',
                                }}
                                onMouseEnter={(e) =>
                                    (e.currentTarget.style.backgroundColor = '#F0F1F3')
                                }
                                onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor = !value
                                        ? '#F0F1F3'
                                        : 'transparent')
                                }
                            >
                                {placeholder}
                            </div>
                        )}
                        {filteredOptions.map((opt, index) => {
                            const val = getOptionValue(opt);
                            const label = getOptionLabel(opt);
                            const isSelected = val === value?.toString();
                            return (
                                <div
                                    key={val || index}
                                    onClick={() => handleSelect(val)}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        color: isSelected ? '#512DA8' : '#1D1F2C',
                                        fontWeight: isSelected ? 600 : 400,
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#ECE6F8' : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected)
                                            e.currentTarget.style.backgroundColor = '#F5F5F5';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected)
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <span>{label}</span>
                                    {isSelected && (
                                        <span style={{ color: '#512DA8', fontSize: '14px' }}>
                                            ✓
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        {filteredOptions.length === 0 && (
                            <div
                                style={{
                                    padding: '12px',
                                    fontSize: '13px',
                                    color: '#858D9D',
                                    textAlign: 'center',
                                }}
                            >
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
